export type OpenBalanceDebtScope = 'all' | 'aged' | 'current';

export interface OpenBalanceInvoice {
  document_date: string;
  open_balance?: number | null;
  balance_status?: string;
}

export interface ScopedOpenBalanceMonth {
  month: string;
  amount: number;
}

export interface ScopedOpenBalanceSummary {
  total: number;
  months: ScopedOpenBalanceMonth[];
  invalidDateCount: number;
}

export function parseOpenInvoiceDate(value: string): Date | null {
  const raw = value.trim();
  if (!raw) return null;

  let year: number;
  let month: number;
  let day: number;
  const iso = raw.match(/^(\d{4})-(\d{2})-(\d{2})(?:[T\s].*)?$/);
  const delimited = raw.match(/^(\d{2})[/-](\d{2})[/-](\d{4})$/);
  const compact = raw.match(/^(\d{2})(\d{2})(\d{4})$/);

  if (iso) {
    year = Number(iso[1]);
    month = Number(iso[2]);
    day = Number(iso[3]);
  } else if (delimited) {
    day = Number(delimited[1]);
    month = Number(delimited[2]);
    year = Number(delimited[3]);
  } else if (compact) {
    day = Number(compact[1]);
    month = Number(compact[2]);
    year = Number(compact[3]);
  } else {
    return null;
  }

  const parsed = new Date(year, month - 1, day);
  if (
    parsed.getFullYear() !== year
    || parsed.getMonth() !== month - 1
    || parsed.getDate() !== day
  ) {
    return null;
  }
  return parsed;
}

export function sortInvoicesOldestFirst<T>(
  invoices: T[],
  getDate: (invoice: T) => string | undefined | null,
): T[] {
  return invoices
    .map((invoice, index) => ({ invoice, index, date: parseOpenInvoiceDate(getDate(invoice) || '') }))
    .sort((left, right) => {
      if (left.date && right.date) {
        return left.date.getTime() - right.date.getTime() || left.index - right.index;
      }
      if (left.date) return -1;
      if (right.date) return 1;
      return left.index - right.index;
    })
    .map(({ invoice }) => invoice);
}

export function summarizeOpenInvoicesByScope(
  invoices: OpenBalanceInvoice[],
  paymentTermsDays: number,
  scope: OpenBalanceDebtScope,
  now = new Date(),
): ScopedOpenBalanceSummary {
  const cutoff = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  cutoff.setDate(cutoff.getDate() - Math.max(paymentTermsDays, 0));

  const monthTotals = new Map<string, number>();
  let invalidDateCount = 0;

  for (const invoice of invoices) {
    if (invoice.balance_status === 'unknown') continue;
    const balance = Math.abs(Number(invoice.open_balance) || 0);
    if (balance <= 0) continue;

    const issueDate = parseOpenInvoiceDate(invoice.document_date);
    if (!issueDate) {
      invalidDateCount += 1;
      continue;
    }

    const isAged = issueDate <= cutoff;
    if (scope === 'aged' && !isAged) continue;
    if (scope === 'current' && isAged) continue;

    const month = `${issueDate.getFullYear()}-${String(issueDate.getMonth() + 1).padStart(2, '0')}`;
    monthTotals.set(month, (monthTotals.get(month) || 0) + balance);
  }

  const months = Array.from(monthTotals.entries())
    .sort(([left], [right]) => left.localeCompare(right))
    .map(([month, amount]) => ({ month, amount: Math.round(amount * 100) / 100 }));

  return {
    total: Math.round(months.reduce((sum, month) => sum + month.amount, 0) * 100) / 100,
    months,
    invalidDateCount,
  };
}
